<template>
  <HireCustomFieldsModal
    :open="open"
    :title="$t('Add details')"
    :fields="fields"
    :field-values="localFieldValues"
    :field-value-fragments="values"
    :loading="loading"
    :saving="saving"
    :failed="failed"
    :hide-footer="hideFooter"
    data-test="hire-worker-custom-fields-modal"
    @update:field-values="localFieldValues = $event"
    @cancel="close"
    @save="save"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import queriesToRefetch from '../../queriesToRefetch'
import { graphql } from '@/gql'
import { CustomFieldTypeValueInput } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { useToast } from '@/composables/useToast'
import HireCustomFieldsModal from '@/components/Hire/HirePageParts/HireCustomFieldsModal.vue'
import {
  diffCustomFieldValueInputs,
  mapCustomFieldValueToCustomFieldValueInput,
} from '@/components/Fieldable/utils'
import { ComplianceModalProps } from '@/components/Hire/HirePageCompliance/modals/complianceModals'

const props = withDefaults(defineProps<ComplianceModalProps>(), {
  open: false,
  hideFooter: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const toast = useToast()

const { result, loading } = useQuery(
  graphql(`
    query HireWorkerCustomFieldsModal($id: ID!, $viewerAccount: ID!) {
      hireInvitation(id: $id) {
        hireId
        answerableCustomFields {
          ...FieldableFormCustomField
        }
        customFieldValues {
          ...FieldableFormCustomFieldValue
        }
      }
    }
  `),
  () => ({ id: props.hireId, viewerAccount: currentAccount.globalId }),
  () => ({ clientId: 'publicApi', enabled: props.open }),
)

const fields = computed(() => result.value?.hireInvitation?.answerableCustomFields ?? [])
const values = computed(() => result.value?.hireInvitation?.customFieldValues ?? [])

const savedFieldValues = ref<CustomFieldTypeValueInput[]>([])
const localFieldValues = ref<CustomFieldTypeValueInput[]>([])

watch(
  [values, () => props.open],
  () => {
    savedFieldValues.value = mapCustomFieldValueToCustomFieldValueInput(values.value)
    localFieldValues.value = [...savedFieldValues.value]
  },
  { immediate: true },
)

const saving = ref(false)
const failed = ref(false)

const { mutate } = useMutation(
  graphql(`
    mutation HireWorkerCustomFieldsSave($input: UpdateWorkerCustomFieldValuesInput!) {
      updateWorkerCustomFieldValues(input: $input) {
        customFieldValues {
          id
        }
      }
    }
  `),
  { clientId: 'publicApi' },
)

const close = () => {
  failed.value = false
  emit('update:open', false)
}

const save = async () => {
  // Untouched fields are left out: submitting them empty would delete stored answers.
  const changed = diffCustomFieldValueInputs(localFieldValues.value, savedFieldValues.value)

  if (changed.length === 0) {
    close()
    return
  }

  saving.value = true
  failed.value = false

  try {
    await mutate({ input: { appliesTo: props.hireId, values: changed } })
    await publicApi.refetchQueries({
      include: [
        ...queriesToRefetch,
        'HireWorkerCustomFieldsModal',
        'HireInvitationPage',
        'HireWorkerCustomFields',
      ],
    })
    toast.success($t('Answers saved'))
    close()
  } catch {
    failed.value = true
  } finally {
    saving.value = false
  }
}
</script>
